import { Col, Icon, Row, Section } from '@advanza/advanza_generic'
import { call } from '@advanza/api'
import { InputField, SelectSimple } from '@advanza/input'
import Modal from '@advanza/modal'
import Cookies from 'js-cookie'
import PropTypes from 'prop-types'
import React from 'react'
import { fetchLabels } from '../../actions/labels'
import { savePromo } from '../../actions/promos'
import { fetchServices } from '../../actions/services'
import EntityComponentContainer from '../../containers/EntityComponentContainer'
import Divider from '../Divider'
import EligibilityRulesInput from '../input/EligibilityRulesInput'
import PromoOfferInput from '../input/PromoOfferInput'
import EntityComponent from '../services/EntityComponent'
import EligibleProvidersList from './EligibleProvidersList'

// 'service_provider_id' is the legacy key kept for backward compatibility
const isSpecificProvidersRule = (rule) =>
    Boolean(rule) && (rule.key === 'specificProviders' || rule.key === 'service_provider_id')

// offers_info is a JSON column: either [{"discount": {...}}, ...] or {"discount": {...}, ...}
const hasConfiguredOffers = (offersInfo) => {
    if (Array.isArray(offersInfo)) {
        return offersInfo.some(
            (offer) => typeof offer === 'object' && offer !== null && Object.keys(offer).length > 0,
        )
    }
    if (offersInfo && typeof offersInfo === 'object') {
        return Object.keys(offersInfo).length > 0
    }
    return false
}

const normalizeRules = (rules) => {
    if (!rules) {
        return null
    }
    return typeof rules === 'object' ? JSON.stringify(rules) : String(rules)
}

// A promo that has never been saved has no promo_id or a temporary string id;
// saved promos get a numeric id from the backend.
const isUnsavedPromoId = (promoId) => !promoId || typeof promoId === 'string'

const PROVIDERS_MODAL_TEXTS = {
    eligible: {
        heading: 'Eligible Providers',
        description: 'Providers that match the eligibility rules for this promo',
    },
    pending: {
        heading: 'Pending Promotion Providers',
        description: 'Providers who have this promo with pending status',
    },
    active: {
        heading: 'Active Promotion Providers',
        description: 'Providers who have this promo with active status',
    },
}

class PromoEntity extends EntityComponent {
    constructor(props) {
        super(props)
        this.state = {
            copiedMd5: false,
            showExpiryDateInfo: false,
            showPromoHashInfo: false,
            eligibleProvidersModalOpen: false,
            selectedModalType: 'eligible', // 'eligible', 'pending', or 'active'
            specificProviderIds: '',
            validatingProviderIds: false,
            providerIdsValidationResult: null,
            promoStats: null, // { eligible, pending, active }
            loadingPromoStats: false,
            generalSettingsExpanded: false, // Collapsed by default
            showAssignConfirmation: false,
            assigningPromo: false,
            assignResult: null,
            showUnassignConfirmation: false,
            unassigningPromo: false,
        }
        this.copyToClipboard = this.copyToClipboard.bind(this)
        this.toggleExpiryDateInfo = this.toggleExpiryDateInfo.bind(this)
        this.togglePromoHashInfo = this.togglePromoHashInfo.bind(this)
        this.handleClickOutside = this.handleClickOutside.bind(this)
        this.openEligibleProvidersModal = this.openEligibleProvidersModal.bind(this)
        this.closeEligibleProvidersModal = this.closeEligibleProvidersModal.bind(this)
        this.handleSpecificProviderIdsChange = this.handleSpecificProviderIdsChange.bind(this)
        this.validateProviderIds = this.validateProviderIds.bind(this)
        this.handleUserTypeChange = this.handleUserTypeChange.bind(this)
        this.fetchPromoStats = this.fetchPromoStats.bind(this)
        this.handleCheckAvailability = this.handleCheckAvailability.bind(this)
        this.toggleGeneralSettings = this.toggleGeneralSettings.bind(this)
        this.handleCancel = this.handleCancel.bind(this)
        this.handleAssignPromo = this.handleAssignPromo.bind(this)
        this.confirmAssignPromo = this.confirmAssignPromo.bind(this)
        this.cancelAssignPromo = this.cancelAssignPromo.bind(this)
        this.handleUnassignPromo = this.handleUnassignPromo.bind(this)
        this.confirmUnassignPromo = this.confirmUnassignPromo.bind(this)
        this.cancelUnassignPromo = this.cancelUnassignPromo.bind(this)
    }

    toggleGeneralSettings() {
        this.setState((prevState) => ({
            generalSettingsExpanded: !prevState.generalSettingsExpanded,
        }))
    }

    handleCancel() {
        const entity = this.getEntity()
        const beforeSave = entity._beforeSave

        // If there's a _beforeSave, revert to it
        if (beforeSave) {
            // Reset entity to _beforeSave state
            this.props.changeEntity(
                {
                    ...beforeSave,
                    _isTouched: false,
                    _errors: {},
                    _saveAttempted: false,
                },
                { dontTouch: true },
            )

            // Reset local state
            const specificProviderIds = beforeSave.specific_provider_ids || ''
            this.setState({
                specificProviderIds,
                validatingProviderIds: false,
                providerIdsValidationResult: null,
                promoStats: null, // Reset stats when canceling
                loadingPromoStats: false,
            })
        } else {
            // If no _beforeSave (new entity), just reset touched state
            this.props.changeEntity(
                {
                    _isTouched: false,
                    _errors: {},
                    _saveAttempted: false,
                },
                { dontTouch: true },
            )

            // Reset local state
            this.setState({
                specificProviderIds: '',
                validatingProviderIds: false,
                providerIdsValidationResult: null,
                promoStats: null,
                loadingPromoStats: false,
            })
        }
    }

    handleCheckAvailability(e) {
        if (e) {
            e.stopPropagation()
        }

        this.setState({ assignResult: null })

        const promo = this.getEntity()
        const promoId = promo.promo_id
        const isNew = isUnsavedPromoId(promoId)

        // If it's a new promo, save it first before checking availability
        if (isNew) {
            this.save()
                .then((response) => {
                    // After saving, check if we got a promo_id from the response
                    // The response might contain the saved entity with promo_id
                    const savedPromoId = response?.data?.promo_id || response?.promo_id

                    if (savedPromoId && typeof savedPromoId === 'number') {
                        // Update the entity with the new promo_id and fetch stats
                        this.onChangeEntity({ promo_id: savedPromoId }, { dontTouch: true })
                        // Small delay to ensure entity update propagates
                        setTimeout(() => {
                            this.fetchPromoStats()
                        }, 50)
                    } else {
                        // Fallback: try fetching stats after a short delay
                        // The entity should be updated by the save action
                        setTimeout(() => {
                            const updatedPromo = this.getEntity()
                            if (
                                updatedPromo.promo_id &&
                                typeof updatedPromo.promo_id === 'number'
                            ) {
                                this.fetchPromoStats()
                            }
                        }, 200)
                    }
                })
                .catch((error) => {
                    console.error('Failed to save promo before checking availability:', error)
                })
        } else {
            this.fetchPromoStats()
        }
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside)

        const entity = this.getEntity()

        // Initialize country_code from cookie if not set
        if (!entity.country_code) {
            const countryCode = Cookies.get('_trustoo_office_country') || 'NL'
            this.onChangeEntity({ country_code: countryCode }, { dontTouch: true })
        }

        // Initialize user_type to '' (All users) if it's null or undefined
        // This ensures the dropdown always has a selected value
        if (entity.user_type === null || entity.user_type === undefined) {
            this.onChangeEntity({ user_type: '' }, { dontTouch: true })
        } else if (entity.user_type === '') {
            // Ensure empty string is explicitly set
            this.onChangeEntity({ user_type: '' }, { dontTouch: true })
        }

        // Initialize specific_provider_ids from eligibility_rules
        const specificProviderIds = this.getSpecificProviderIdsValue()
        if (specificProviderIds) {
            this.setState({ specificProviderIds })
        }

        // Fetch services if not already loaded (needed for eligibility rules preview)
        const { entities } = this.props
        const { services } = entities || {}
        if (!services || Object.keys(services).length === 0) {
            if (this.props.fetchServices) {
                this.props.fetchServices('all')
            }
        }

        // Fetch prospect labels for eligibility rules preview
        if (this.props.fetchLabels) {
            this.props.fetchLabels({
                searchKey: 'serviceProviders',
                categories: ['serviceProviders'],
                noLimit: true,
            })
        }
    }

    componentDidUpdate(prevProps) {
        // Extract specific_provider_ids from entity
        const currentEntity = this.getEntity()

        if (
            currentEntity.specific_provider_ids &&
            this.state.specificProviderIds !== currentEntity.specific_provider_ids
        ) {
            this.setState({ specificProviderIds: currentEntity.specific_provider_ids })
        }

        // Check if eligibility_rules, user_type, country_code, or expiry_date have changed - if so, reset stats to show "Check availability" again
        const prevEntity = prevProps.entity
        const currentEligibilityRules = currentEntity.eligibility_rules
        const prevEligibilityRules = prevEntity?.eligibility_rules
        const currentUserType = currentEntity.user_type
        const prevUserType = prevEntity?.user_type
        const currentCountryCode = currentEntity.country_code
        const prevCountryCode = prevEntity?.country_code
        const currentExpiryDate = currentEntity.expiry_date
        const prevExpiryDate = prevEntity?.expiry_date

        const currentRulesStr = normalizeRules(currentEligibilityRules)
        const prevRulesStr = normalizeRules(prevEligibilityRules)

        // If rules, userType, countryCode, or expiryDate have changed, reset stats
        if (
            (currentRulesStr !== prevRulesStr ||
                currentUserType !== prevUserType ||
                currentCountryCode !== prevCountryCode ||
                currentExpiryDate !== prevExpiryDate) &&
            (this.state.promoStats !== null || this.state.assignResult !== null)
        ) {
            this.setState({
                promoStats: null,
                loadingPromoStats: false,
                assignResult: null,
            })
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside)
    }

    handleClickOutside(event) {
        if (this.expiryDateInfoRef && !this.expiryDateInfoRef.contains(event.target)) {
            this.setState({ showExpiryDateInfo: false })
        }
        if (this.promoHashInfoRef && !this.promoHashInfoRef.contains(event.target)) {
            this.setState({ showPromoHashInfo: false })
        }
    }

    toggleExpiryDateInfo(e) {
        e.stopPropagation()
        this.setState({ showExpiryDateInfo: !this.state.showExpiryDateInfo })
    }

    togglePromoHashInfo(e) {
        e.stopPropagation()
        this.setState({ showPromoHashInfo: !this.state.showPromoHashInfo })
    }

    openEligibleProvidersModal(modalType = 'eligible') {
        this.setState({
            eligibleProvidersModalOpen: true,
            selectedModalType: modalType,
        })
    }

    closeEligibleProvidersModal() {
        this.setState({
            eligibleProvidersModalOpen: false,
            selectedModalType: 'eligible',
        })
    }

    shouldShowSpecificProviderIdsInput() {
        const entity = this.getEntity()

        // Show if user_type is "specific"
        return entity.user_type === 'specific'
    }

    getSpecificProviderIdsValue() {
        const entity = this.getEntity()

        // First check state
        if (this.state.specificProviderIds) {
            return this.state.specificProviderIds
        }

        // Then check eligibility_rules for specificProviders rule
        const eligibilityRules = entity.eligibility_rules || []
        if (Array.isArray(eligibilityRules)) {
            const serviceProviderRule = eligibilityRules.find(isSpecificProvidersRule)
            if (serviceProviderRule && serviceProviderRule.value) {
                return serviceProviderRule.value
            }
        }

        // Fallback to entity.specific_provider_ids for backward compatibility
        if (entity.specific_provider_ids) {
            return entity.specific_provider_ids
        }

        return ''
    }

    handleSpecificProviderIdsChange(e) {
        const value = e.target.value
        this.setState({ specificProviderIds: value })
        // Update entity with the provider IDs in eligibility_rules
        const entity = this.getEntity()

        // Clear error if user starts typing
        const errors = { ...(entity._errors || {}) }
        if (errors.specific_provider_ids) {
            delete errors.specific_provider_ids
        }

        // Update eligibility_rules to include specificProviders rule
        const eligibilityRules = Array.isArray(entity.eligibility_rules)
            ? [...entity.eligibility_rules]
            : []

        // Remove existing specificProviders rule if it exists
        const filteredRules = eligibilityRules.filter(
            (rule) => !rule || rule.key !== 'specificProviders',
        )

        // Add new specificProviders rule if value is not empty
        const updatedRules = value.trim()
            ? [
                  ...filteredRules,
                  {
                      key: 'specificProviders',
                      operator: '=',
                      value: value.trim(),
                  },
              ]
            : filteredRules

        this.onChangeEntity({
            eligibility_rules: updatedRules,
            _errors: Object.keys(errors).length > 0 ? errors : undefined,
        })
    }

    handleUserTypeChange(e) {
        const userType = e.target.value
        const entity = this.getEntity()

        // Normalize empty string to ensure it's saved as '' (not null)
        const normalizedUserType = userType === '' ? '' : userType

        // Clear specific_provider_ids error if user changes away from "specific"
        const errors = { ...(entity._errors || {}) }
        if (normalizedUserType !== 'specific' && errors.specific_provider_ids) {
            delete errors.specific_provider_ids
        }

        // Remove specificProviders rule from eligibility_rules when user_type is not 'specific'
        let eligibilityRules = Array.isArray(entity.eligibility_rules)
            ? [...entity.eligibility_rules]
            : []

        if (normalizedUserType !== 'specific') {
            eligibilityRules = eligibilityRules.filter((rule) => !isSpecificProvidersRule(rule))
        }

        this.onChangeEntity({
            user_type: normalizedUserType,
            eligibility_rules: eligibilityRules,
            _errors: Object.keys(errors).length > 0 ? errors : undefined,
        })
    }

    async validateProviderIds() {
        const { specificProviderIds } = this.state
        if (!specificProviderIds.trim()) {
            this.setState({
                providerIdsValidationResult: {
                    valid: false,
                    message: 'Please enter provider IDs',
                },
            })
            return
        }

        // Validate format: comma-separated integers
        const ids = specificProviderIds
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id !== '')

        // Check if all are valid integers
        const invalidIds = ids.filter((id) => isNaN(parseInt(id, 10)) || parseInt(id, 10) <= 0)
        if (invalidIds.length > 0) {
            this.setState({
                providerIdsValidationResult: {
                    valid: false,
                    message: `Invalid IDs: ${invalidIds.join(', ')}. Only positive integers are allowed.`,
                },
            })
            return
        }

        // Convert to integers
        const providerIds = ids.map((id) => parseInt(id, 10))

        this.setState({ validatingProviderIds: true, providerIdsValidationResult: null })

        try {
            const response = await call('office/promos/validate-provider-ids', {
                json: { provider_ids: providerIds },
            })

            this.setState({
                validatingProviderIds: false,
                providerIdsValidationResult: {
                    valid: true,
                    count: response.count || 0,
                    message: `Found ${response.count || 0} matching provider(s)`,
                },
            })
        } catch (error) {
            this.setState({
                validatingProviderIds: false,
                providerIdsValidationResult: {
                    valid: false,
                    message: error.message || 'Error validating provider IDs',
                },
            })
        }
    }

    save(params) {
        const entity = this.getEntity()

        // Validate that service_provider_id rule exists in eligibility_rules when user_type is "specific"
        if (entity.user_type === 'specific') {
            const providerIdsRaw = this.getSpecificProviderIdsValue()

            // Normalize providerIds: convert array to comma-separated string, ensure it's a string
            let providerIds = ''
            if (Array.isArray(providerIdsRaw)) {
                providerIds = providerIdsRaw.join(', ')
            } else if (typeof providerIdsRaw === 'string') {
                providerIds = providerIdsRaw
            } else if (providerIdsRaw != null) {
                // Convert other types to string
                providerIds = String(providerIdsRaw)
            }

            // Trim the string value
            providerIds = providerIds.trim()

            if (!providerIds) {
                // Set error on the entity field only
                this.onChangeEntity({
                    _errors: {
                        ...(entity._errors || {}),
                        specific_provider_ids:
                            'Provider IDs are required when user type is "Specific"',
                    },
                    _saveAttempted: true,
                })
                // Return resolved promise to prevent uncaught error, error is shown on field
                return Promise.resolve()
            }

            // Ensure specificProviders rule exists in eligibility_rules before saving
            const eligibilityRules = Array.isArray(entity.eligibility_rules)
                ? [...entity.eligibility_rules]
                : []
            const hasServiceProviderRule = eligibilityRules.some(isSpecificProvidersRule)

            if (hasServiceProviderRule) {
                // Update existing specificProviders rule with current value
                const updatedRules = eligibilityRules.map((rule) => {
                    if (isSpecificProvidersRule(rule)) {
                        return {
                            key: 'specificProviders',
                            operator: '=',
                            value: providerIds,
                        }
                    }
                    return rule
                })
                this.onChangeEntity({
                    eligibility_rules: updatedRules,
                })
            } else {
                // Add specificProviders rule to eligibility_rules
                eligibilityRules.push({
                    key: 'specificProviders',
                    operator: '=',
                    value: providerIds,
                })
                this.onChangeEntity({
                    eligibility_rules: eligibilityRules,
                })
            }
        }

        // Validate that offers_info has at least one offer
        if (!hasConfiguredOffers(entity.offers_info)) {
            // Set error on the entity field only
            this.onChangeEntity({
                _errors: {
                    ...(entity._errors || {}),
                    offers_info: 'At least one offer must be set up',
                },
                _saveAttempted: true,
            })
            // Return resolved promise to prevent uncaught error, error is shown on field
            return Promise.resolve()
        }

        // Override save to handle errors silently (no error messages displayed)
        return super.save(params).catch(() => {
            // Clear any error state that was set
            if (entity._error) {
                this.onChangeEntity({ _error: undefined }, { dontTouch: true })
            }
            // Silently swallow the error - no user-facing error message
            // The backend will have logged the issue
        })
    }

    copyToClipboard(text) {
        const copyPromise =
            navigator.clipboard && navigator.clipboard.writeText
                ? navigator.clipboard.writeText(text)
                : new Promise((resolve, reject) => {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea')
                      textArea.value = text
                      textArea.style.position = 'fixed'
                      textArea.style.opacity = '0'
                      document.body.appendChild(textArea)
                      textArea.select()
                      try {
                          document.execCommand('copy')
                          resolve()
                      } catch (err) {
                          reject(err)
                      }
                      document.body.removeChild(textArea)
                  })

        copyPromise
            .then(() => {
                // Set state to show checkmark
                this.setState({ copiedMd5: true })
                // Reset after 1 second
                setTimeout(() => {
                    this.setState({ copiedMd5: false })
                }, 1000)
            })
            .catch((err) => {
                console.error('Failed to copy to clipboard:', err)
            })
    }

    renderOffersPreview() {
        const entity = this.getEntity()
        // JSON columns are automatically decoded to arrays/objects by CakePHP
        const offersInfo = entity.offers_info

        if (!offersInfo) {
            return null
        }

        // Only handle arrays/objects, no string parsing
        if (!Array.isArray(offersInfo) && typeof offersInfo !== 'object') {
            return null
        }

        if (Array.isArray(offersInfo) && offersInfo.length === 0) {
            return null
        }

        // Handle array format: [{"discount": {...}}, {"leads": {...}}]
        const offers = Array.isArray(offersInfo) ? offersInfo : [offersInfo]
        const summaryParts = []

        offers.forEach((offerObj) => {
            if (typeof offerObj === 'object' && offerObj !== null) {
                Object.keys(offerObj).forEach((offerType) => {
                    const offerData = offerObj[offerType]
                    if (typeof offerData === 'object' && offerData !== null) {
                        const value =
                            offerData.value !== undefined && offerData.value !== null
                                ? offerData.value
                                : ''
                        const type = offerData.type || ''
                        const promoLength =
                            offerData.promo_length !== undefined && offerData.promo_length !== null
                                ? offerData.promo_length
                                : null
                        const expiryDate = offerData.expiryDate || ''

                        // Format based on offer type
                        if (offerType === 'discount') {
                            // Subscription discount
                            let valueDisplay = value
                            if (type === 'percentage') {
                                valueDisplay = `${value}%`
                            } else if (type === 'amount') {
                                valueDisplay = `€${value}`
                            }
                            if (promoLength !== null && promoLength !== undefined) {
                                if (promoLength === 0) {
                                    summaryParts.push(`${valueDisplay} discount (one-time)`)
                                } else {
                                    summaryParts.push(
                                        `${valueDisplay} discount for ${promoLength} month${promoLength === 1 ? '' : 's'}`,
                                    )
                                }
                            } else {
                                summaryParts.push(`${valueDisplay} discount (ongoing)`)
                            }
                        } else if (offerType === 'leads') {
                            // Recurring or one-time free leads
                            const isOneTime = promoLength === 0 || expiryDate
                            if (isOneTime) {
                                if (expiryDate) {
                                    summaryParts.push(
                                        `${value} one-time free leads (expires ${expiryDate})`,
                                    )
                                } else {
                                    summaryParts.push(`${value} one-time free leads`)
                                }
                            } else {
                                // Recurring leads
                                if (promoLength !== null && promoLength !== undefined) {
                                    summaryParts.push(
                                        `${value} recurring free leads for ${promoLength} month${promoLength === 1 ? '' : 's'}`,
                                    )
                                } else {
                                    summaryParts.push(`${value} recurring free leads (ongoing)`)
                                }
                            }
                        }
                    }
                })
            }
        })

        if (summaryParts.length === 0) {
            return null
        }

        return (
            <Section n>
                <div
                    style={{
                        padding: '12px 16px',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        borderRadius: '4px',
                        fontSize: '14px',
                    }}>
                    <strong>Promo offer:</strong> {summaryParts.join(' + ')}
                </div>
            </Section>
        )
    }

    fetchPromoStats() {
        const promo = this.getEntity()
        const promoId = promo.promo_id
        if (!promoId) {
            return
        }

        // Check if eligibility rules are empty AND userType is null/empty AND country_code is null/empty
        const eligibilityRules = promo.eligibility_rules
        const userType = promo.user_type
        const countryCode = promo.country_code
        const hasEmptyRules =
            !eligibilityRules ||
            (Array.isArray(eligibilityRules) && eligibilityRules.length === 0) ||
            (typeof eligibilityRules === 'object' && Object.keys(eligibilityRules).length === 0)
        const hasEmptyUserType = !userType || userType === '' || userType === null
        const hasEmptyCountryCode = !countryCode || countryCode === '' || countryCode === null

        // If all are empty, show special message without querying database
        if (hasEmptyRules && hasEmptyUserType && hasEmptyCountryCode) {
            this.setState({
                promoStats: {
                    eligible_to_everyone: true,
                    message: 'Eligible to everyone without restrictions',
                    eligible: null,
                    pending: 0,
                    active: 0,
                },
                loadingPromoStats: false,
            })
            return
        }

        // No early-return when only the eligibility rules are empty: user_type
        // (premium, churned, basic, …) and country_code are valid targeting
        // criteria on their own. The backend get-promo-stats applies the
        // user_type / country filters even with no extra rules, so we must let
        // it compute the real count here too — otherwise the count view would
        // show 0 while the providers modal (which queries the backend directly)
        // correctly lists the matching providers, and the "Assign to eligible
        // providers" button (gated on eligible > 0) would never appear.

        // Set loading state
        this.setState({ loadingPromoStats: true })

        call(`office/promos/get-promo-stats/${promoId}`)
            .then((response) => {
                const stats = response.data || response
                // Check if response indicates eligible to everyone
                if (stats.eligible_to_everyone) {
                    this.setState({
                        promoStats: {
                            eligible_to_everyone: true,
                            message: stats.message || 'Eligible to everyone without restrictions',
                            eligible: null,
                            pending: stats.pending || 0,
                            active: stats.active || 0,
                        },
                        loadingPromoStats: false,
                    })
                } else {
                    this.setState({
                        promoStats: {
                            eligible: stats.eligible || 0,
                            pending: stats.pending || 0,
                            active: stats.active || 0,
                        },
                        loadingPromoStats: false,
                    })
                }
            })
            .catch((error) => {
                console.error(`Failed to fetch stats for promo ${promoId}:`, error)
                this.setState({ loadingPromoStats: false })
            })
    }

    handleAssignPromo() {
        const { promoStats } = this.state
        const eligible = promoStats?.eligible || 0
        if (eligible > 0) {
            this.setState({ showAssignConfirmation: true, assignResult: null })
        }
    }

    confirmAssignPromo() {
        const promo = this.getEntity()
        const promoId = promo.promo_id
        const { promoStats } = this.state
        const eligible = promoStats?.eligible || 0

        if (!promoId || eligible === 0) {
            return
        }

        this.setState({ showAssignConfirmation: false, assigningPromo: true })

        call(`office/promos/assign-to-eligible/${promoId}`, {
            method: 'POST',
        })
            .then((response) => {
                const result = response?.data || response || {}
                // Refresh stats after assignment
                this.fetchPromoStats()
                this.setState({
                    assigningPromo: false,
                    assignResult: {
                        assigned: result.assigned || 0,
                        skippedWithActive: result.skipped_with_active || 0,
                        message:
                            result.message ||
                            `Assigned promo to ${result.assigned || 0} provider(s).`,
                    },
                })
            })
            .catch((error) => {
                console.error('Failed to assign promo:', error)
                this.setState({ assigningPromo: false })
                alert(error.message || 'Failed to assign promo to eligible providers')
            })
    }

    cancelAssignPromo() {
        this.setState({ showAssignConfirmation: false })
    }

    handleUnassignPromo() {
        const { promoStats } = this.state
        const pending = promoStats?.pending || 0
        const active = promoStats?.active || 0

        // Show confirmation if there are any assigned promos (pending or active)
        if (pending > 0 || active > 0) {
            this.setState({ showUnassignConfirmation: true })
        }
    }

    confirmUnassignPromo() {
        const promo = this.getEntity()
        const promoId = promo.promo_id

        if (!promoId) {
            return
        }

        this.setState({ showUnassignConfirmation: false, unassigningPromo: true })

        call(`office/promos/unassign-pending/${promoId}`, {
            method: 'POST',
        })
            .then((response) => {
                // Refresh stats after unassignment
                this.fetchPromoStats()
                this.setState({ unassigningPromo: false })
            })
            .catch((error) => {
                console.error('Failed to unassign promo:', error)
                this.setState({ unassigningPromo: false })
                alert(error.message || 'Failed to unassign promos')
            })
    }

    cancelUnassignPromo() {
        this.setState({ showUnassignConfirmation: false })
    }

    renderPromoStats() {
        const { promoStats, loadingPromoStats } = this.state
        const promo = this.getEntity()
        const promoId = promo.promo_id
        const isNew = isUnsavedPromoId(promoId)

        // Don't show check availability button for new promos
        if (isNew) {
            return null
        }

        // If stats haven't been fetched yet, show link
        if (!promoStats && !loadingPromoStats) {
            return (
                <button
                    type="button"
                    onClick={this.handleCheckAvailability}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#1976d2',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                    }}>
                    Check availability
                </button>
            )
        }

        // If loading, show loading message
        if (loadingPromoStats) {
            return (
                <span
                    style={{
                        color: '#666',
                        fontSize: 'inherit',
                        whiteSpace: 'nowrap',
                    }}>
                    Checking...
                </span>
            )
        }

        // Check if promo is eligible to everyone
        if (promoStats && promoStats.eligible_to_everyone) {
            return (
                <div style={{ fontSize: '14px', color: '#4caf50', fontWeight: 'bold' }}>
                    {promoStats.message || 'Eligible to everyone without restrictions'}
                </div>
            )
        }

        // Show counts if stats are available
        const { eligible, pending, active } = promoStats || { eligible: 0, pending: 0, active: 0 }

        // Check if promo has at least one offer configured
        const hasOffers = hasConfiguredOffers(promo.offers_info)

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div
                    style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}>
                    <span
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            this.openEligibleProvidersModal('eligible')
                        }}
                        title="Providers that match this promo's targeting rules and do not already have it Pending or Active. A provider can still be assigned this promo manually from their profile page even if it is not in this list."
                        style={{
                            color: '#1976d2',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                        }}>
                        Eligible: {eligible}
                    </span>
                    <span
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            this.openEligibleProvidersModal('pending')
                        }}
                        title="Providers that have this promo assigned but have not yet started using it."
                        style={{
                            color: '#ff9800',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                        }}>
                        Pending: {pending}
                    </span>
                    <span
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            this.openEligibleProvidersModal('active')
                        }}
                        title="Providers currently using this promo."
                        style={{
                            color: '#4caf50',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                        }}>
                        Active: {active}
                    </span>
                </div>
                {eligible > 0 && hasOffers && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            this.handleAssignPromo()
                        }}
                        disabled={this.state.assigningPromo}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#ff9800',
                            textDecoration: 'underline',
                            cursor: this.state.assigningPromo ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            alignSelf: 'flex-start',
                            opacity: this.state.assigningPromo ? 0.6 : 1,
                        }}>
                        {this.state.assigningPromo
                            ? 'Assigning...'
                            : 'Assign to eligible providers'}
                    </button>
                )}
                {(pending > 0 || active > 0) && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            this.handleUnassignPromo()
                        }}
                        disabled={this.state.unassigningPromo}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#dc3545',
                            textDecoration: 'underline',
                            cursor: this.state.unassigningPromo ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            alignSelf: 'flex-start',
                            opacity: this.state.unassigningPromo ? 0.6 : 1,
                        }}>
                        {this.state.unassigningPromo ? 'Unassigning...' : 'Unassign all promos'}
                    </button>
                )}
                {this.state.assignResult && (
                    <p
                        style={{
                            margin: 0,
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: this.state.assignResult.assigned > 0 ? '#4caf50' : '#ff9800',
                        }}>
                        {this.state.assignResult.message}
                        {this.state.assignResult.assigned === 0 &&
                            this.state.assignResult.skippedWithActive > 0 &&
                            ' (a provider can only hold one active promo at a time).'}
                    </p>
                )}
            </div>
        )
    }

    renderEligibilityRulesPreview() {
        const { entity: promo, entities } = this.props
        const eligibilityRules = promo.eligibility_rules
        // Services are passed from EntityComponentContainer as entities.services
        const servicesMap = entities?.services || {}
        const userType = promo.user_type
        const expiryDate = promo.expiry_date

        // Format user type (will be bolded)
        let userTypeDisplay = 'all users'
        if (userType) {
            if (userType === 'basic') {
                userTypeDisplay = 'Basic users'
            } else if (userType === 'other_non_signed') {
                userTypeDisplay = 'Other non signed users'
            } else if (userType === 'all_not_signed') {
                userTypeDisplay = 'All not signed users'
            } else if (userType === 'churned') {
                userTypeDisplay = 'Churned users'
            } else if (userType === 'premium') {
                userTypeDisplay = 'Premium users'
            } else if (userType === 'specific') {
                const providerIds = promo.specific_provider_ids || ''
                if (providerIds) {
                    userTypeDisplay = `Specific users (IDs: ${providerIds})`
                } else {
                    userTypeDisplay = 'Specific users'
                }
            }
        }

        // Format eligibility rules with JSX elements for bold formatting
        let eligibilityRulesDisplay = null
        if (Array.isArray(eligibilityRules) && eligibilityRules.length > 0) {
            const formatRulePreview = (rule, index) => {
                const { key, operator, value } = rule
                if (!key || !operator || value === undefined || value === null || value === '') {
                    return null
                }

                // For services, use colon instead of equals
                const useColon = key === 'services' && operator === '='
                const operatorSymbol = useColon ? ':' : operator

                let valueDisplay = null
                let shouldBoldValue = false

                if (key === 'isSigned') {
                    valueDisplay = value
                } else if (key === 'ProspectValue') {
                    shouldBoldValue = true
                    const labelsMap = this.props.prospectLabelsMap || {}
                    if (Array.isArray(value)) {
                        valueDisplay = value
                            .map((id) => labelsMap[id]?.description ?? id)
                            .join(', ')
                    } else {
                        valueDisplay = labelsMap[value]?.description ?? value.toString()
                    }
                } else if (key === 'services') {
                    shouldBoldValue = true
                    // Convert service IDs to service names
                    let serviceIds = value
                    if (typeof value === 'string') {
                        try {
                            serviceIds = JSON.parse(value)
                        } catch (e) {
                            serviceIds = [parseInt(value, 10)]
                        }
                    }
                    if (!Array.isArray(serviceIds)) {
                        serviceIds = [serviceIds]
                    }
                    serviceIds = serviceIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))

                    const serviceNames = serviceIds.map((serviceId) => {
                        // Try both numeric and string keys since services might be keyed differently
                        // Also try accessing by service_id if services are stored as objects with service_id property
                        let service = servicesMap[serviceId] || servicesMap[String(serviceId)]

                        // If not found, try searching through all services
                        if (!service && Object.keys(servicesMap).length > 0) {
                            const serviceArray = Object.values(servicesMap)
                            service = serviceArray.find(
                                (s) =>
                                    s.service_id === serviceId ||
                                    String(s.service_id) === String(serviceId),
                            )
                        }

                        if (service) {
                            return service.name_providers || service.name || `Service ${serviceId}`
                        }
                        // If service not found, return fallback
                        return `Service ${serviceId}`
                    })
                    valueDisplay = serviceNames.join(', ')
                } else if (key === 'premiumSignedDate') {
                    shouldBoldValue = true
                    if (Array.isArray(value)) {
                        valueDisplay = value.join(', ')
                    } else {
                        valueDisplay = value.toString()
                    }
                } else {
                    if (Array.isArray(value)) {
                        valueDisplay = value.join(', ')
                    } else {
                        valueDisplay = value.toString()
                    }
                }

                // Format key for display (lowercase with spaces)
                let keyDisplay = key.toLowerCase()
                if (keyDisplay === 'prospectvalue') {
                    keyDisplay = 'prospect value'
                } else if (keyDisplay === 'premiumsigneddate') {
                    keyDisplay = 'premium signed date'
                } else if (keyDisplay === 'specificproviders') {
                    keyDisplay = 'specific providers'
                } else if (keyDisplay === 'issigned') {
                    keyDisplay = 'is signed'
                }

                // Format based on key type for natural language
                if (keyDisplay === 'services') {
                    const prefix = operator === '!=' ? 'not from services' : 'from services'
                    return (
                        <span key={`rule-${index}`}>
                            {' '}
                            {prefix}: <strong>{valueDisplay}</strong>
                        </span>
                    )
                } else if (keyDisplay === 'prospect value') {
                    const pvOperator = operator === '!=' ? 'is not' : 'is'
                    return (
                        <span key={`rule-${index}`}>
                            {' '}
                            with <strong>{keyDisplay}</strong> {pvOperator}{' '}
                            <strong>{valueDisplay}</strong>
                        </span>
                    )
                } else if (keyDisplay === 'premium signed date') {
                    return (
                        <span key={`rule-${index}`}>
                            {' '}
                            where <strong>{keyDisplay}</strong> {operatorSymbol}{' '}
                            <strong>{valueDisplay}</strong>
                        </span>
                    )
                } else {
                    return (
                        <span key={`rule-${index}`}>
                            {' '}
                            where {keyDisplay} {operatorSymbol}{' '}
                            {shouldBoldValue ? <strong>{valueDisplay}</strong> : valueDisplay}
                        </span>
                    )
                }
            }

            // Check if there's a premiumSignedDate rule
            const hasPremiumSignedDate = eligibilityRules.some(
                (rule) => rule.key && rule.key.toLowerCase() === 'premiumsigneddate',
            )

            const previews = eligibilityRules
                .map((rule, index) => {
                    const preview = formatRulePreview(rule, index)
                    if (!preview) {
                        return null
                    }

                    // Add fullstop after services rule if there's no premiumSignedDate rule
                    if (
                        rule.key &&
                        rule.key.toLowerCase() === 'services' &&
                        !hasPremiumSignedDate
                    ) {
                        return <span key={`rule-${index}-wrapper`}>{preview}.</span>
                    }

                    return preview
                })
                .filter((preview) => preview !== null)

            if (previews.length > 0) {
                eligibilityRulesDisplay = previews
            }
        }

        // Format expiry date
        let expiryDateDisplay = null
        if (expiryDate) {
            // Format date - expiryDate might be a string or Date object
            let formattedDate = expiryDate
            if (typeof expiryDate === 'string') {
                // Try to format the date string
                try {
                    const date = new Date(expiryDate)
                    if (!isNaN(date.getTime())) {
                        formattedDate = date.toLocaleDateString()
                    }
                } catch (e) {
                    // Keep original string if parsing fails
                }
            } else if (expiryDate instanceof Date) {
                formattedDate = expiryDate.toLocaleDateString()
            }
            expiryDateDisplay = (
                <>
                    {' '}
                    Promo expires at <strong>{formattedDate}</strong>.
                </>
            )
        } else {
            expiryDateDisplay = ' Promo has no expiry date.'
        }

        // Build the complete preview message with JSX
        return (
            <div
                style={{
                    marginBottom: '16px',
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '14px',
                }}>
                <strong>Preview:</strong> Promo available for <strong>{userTypeDisplay}</strong>
                {eligibilityRulesDisplay && eligibilityRulesDisplay.length > 0
                    ? eligibilityRulesDisplay
                    : ' with no restrictions'}
                {expiryDateDisplay}
            </div>
        )
    }

    renderInput(name, saveAfterChange, additionalProps = {}) {
        // Override to support additional props like renderAfterRules
        const fieldOptions = this.getFields()[name]
        if (fieldOptions.isHidden) {
            return null
        }
        const entity = this.getEntity()
        const errors = entity && entity._saveAttempted === true ? entity._errors || {} : {}
        const changeFunc = saveAfterChange ? this.onChangeAndSave : this.onChange
        const entityKey = fieldOptions.name || name

        // Override user_type to use custom onChange handler
        if (name === 'user_type') {
            const rawValue = entity[entityKey]
            // Always default to '' (All users) if value is null or undefined
            // Ensure it's explicitly a string, not null/undefined
            // Use a special marker to ensure empty string is treated as a valid selection
            const selectValue =
                rawValue === null || rawValue === undefined || rawValue === ''
                    ? ''
                    : String(rawValue)

            // Check if a value is actually set (including empty string for "All users")
            const hasValue = rawValue !== null && rawValue !== undefined

            return (
                <SelectSimple
                    value={selectValue}
                    alwaysControlValue
                    emptyOption={false}
                    hidePlaceholder={hasValue}
                    placeholder={fieldOptions.msg}
                    options={fieldOptions.options}
                    name={entityKey}
                    {...fieldOptions}
                    onChange={this.handleUserTypeChange}
                />
            )
        }

        // Handle eligibilityRules with renderAfterRules prop
        if (fieldOptions.type === 'eligibilityRules') {
            // Extract key from additionalProps if present (React keys must be passed directly, not through spread)
            const { key, ...restAdditionalProps } = additionalProps
            // JSON columns return arrays/objects, pass empty array if null/undefined
            const eligibilityRulesValue = entity[entityKey] || []
            return (
                <EligibilityRulesInput
                    key={key}
                    value={eligibilityRulesValue}
                    error={errors[entityKey]}
                    onChange={changeFunc}
                    {...fieldOptions}
                    {...restAdditionalProps}
                    name={entityKey}
                />
            )
        }

        // Handle offers_info to clear error when offers are changed
        // Error is displayed in the header, not inside the component
        if (name === 'offers_info' && fieldOptions.type === 'promoOffer') {
            const customChangeFunc = (value) => {
                // Clear error if offers_info is being changed
                const currentErrors = { ...(entity._errors || {}) }
                if (currentErrors.offers_info) {
                    delete currentErrors.offers_info
                    this.onChangeEntity({
                        _errors: Object.keys(currentErrors).length > 0 ? currentErrors : undefined,
                    })
                }
                // Call the original change function
                changeFunc(value)
            }
            // Render PromoOfferInput directly with custom onChange
            // Don't pass error prop - error is shown in header
            const entityId =
                entity && (entity.promo_id || entity.id || entity[Object.keys(entity)[0]])
            return (
                <PromoOfferInput
                    key={`promo-offer-${entityId || 'new'}-${entityKey}`}
                    value={entity[entityKey] || ''}
                    onChange={customChangeFunc}
                    {...fieldOptions}
                    name={entityKey}
                />
            )
        }

        // For all other field types, use parent's renderInput
        return super.renderInput(name, saveAfterChange)
    }

    editFields() {
        const { entity: promo } = this.props
        const isNew = typeof promo.promo_id === 'string'
        return {
            name: {
                type: 'text',
                msg: 'Name',
            },
            identifier: {
                type: 'text',
                msg: 'Identifier',
                readOnly: !isNew,
            },
            md5_identifier: {
                type: 'text',
                msg: 'Promo Hash Identifier',
                isHidden: true,
                readOnly: true,
            },
            description: {
                type: 'textArea',
                msg: 'Description',
                maxRows: 2,
                style: {
                    backgroundColor: '#ffffff',
                    border: '1px solid #b6b9cd',
                    borderRadius: '4px',
                    padding: '12px 14px',
                    width: '100%',
                    boxSizing: 'border-box',
                    lineHeight: '20px',
                    fontFamily: 'inherit',
                    fontSize: '16px',
                },
            },
            user_type: {
                type: 'selectSimple',
                msg: 'User Type',
                emptyOption: false, // Disable emptyOption since we have "All users" as first option
                options: [
                    { value: '', name: 'All users' },
                    { value: 'basic', name: 'Basic' },
                    { value: 'other_non_signed', name: 'Other non signed' },
                    { value: 'all_not_signed', name: 'All not signed' },
                    { value: 'churned', name: 'Churned' },
                    { value: 'premium', name: 'Premium' },
                    { value: 'specific', name: 'Specific' },
                ],
            },
            expiry_date: {
                type: 'date',
                msg: 'Expiry Date (optional)',
            },
            country_code: {
                type: 'select',
                className: 'select-min fsize lg',
                placeholder: 'Country',
                options: [
                    { value: 'NL', title: 'NL' },
                    { value: 'BE', title: 'BE' },
                    { value: 'DE', title: 'DE' },
                    { value: 'ES', title: 'ES' },
                ],
            },
            offers_info: {
                type: 'promoOffer',
                msg: 'Offers',
            },
            eligibility_rules: {
                type: 'eligibilityRules',
                msg: 'Eligibility Rules',
            },
        }
    }

    render() {
        const { entity: promo } = this.props

        return (
            <div onKeyPress={({ key, ctrlKey }) => ctrlKey && key === 'Enter' && this.save()}>
                {/* Promo Hash Section */}
                {promo.md5_identifier && (
                    <>
                        <h3>Promo Hash</h3>
                        <Section n>
                            <Row className="v-center">
                                <Col className="grow">
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}>
                                        <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                            &promo={promo.md5_identifier}
                                        </span>
                                        {this.state.copiedMd5 ? (
                                            <div
                                                style={{
                                                    position: 'relative',
                                                    display: 'inline-block',
                                                }}>
                                                <Icon
                                                    small
                                                    style={{ cursor: 'pointer', color: 'green' }}>
                                                    check
                                                </Icon>
                                                <div
                                                    className="balloon animate-in"
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: '100%',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        marginBottom: '5px',
                                                        padding: '4px 8px',
                                                        backgroundColor: '#333',
                                                        color: '#fff',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 1000,
                                                    }}>
                                                    Copied to clipboard!
                                                </div>
                                            </div>
                                        ) : (
                                            <Icon
                                                small
                                                style={{ cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    this.copyToClipboard(
                                                        `&promo=${promo.md5_identifier}`,
                                                    )
                                                }}>
                                                content_copy
                                            </Icon>
                                        )}
                                        <div
                                            ref={(el) => (this.promoHashInfoRef = el)}
                                            style={{ position: 'relative' }}>
                                            <Icon
                                                style={{ cursor: 'pointer', color: '#666' }}
                                                fontSize={18}
                                                onClick={this.togglePromoHashInfo}>
                                                info
                                            </Icon>
                                            {this.state.showPromoHashInfo && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        marginTop: '8px',
                                                        padding: '12px 16px',
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                        zIndex: 1000,
                                                        minWidth: '280px',
                                                        maxWidth: '350px',
                                                    }}>
                                                    <p style={{ margin: 0, fontSize: '14px' }}>
                                                        Append this after the token in the HubSpot
                                                        campaign
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Section>
                        <Divider m />
                    </>
                )}

                {/* Settings - Collapsible */}
                <Section
                    n
                    style={{
                        marginBottom: 0,
                        marginTop: 0,
                    }}>
                    <Row
                        between="xs"
                        middle="xs"
                        style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            backgroundColor: '#f5f5f5',
                            borderBottom: this.state.generalSettingsExpanded
                                ? '1px solid #ddd'
                                : 'none',
                        }}
                        onClick={this.toggleGeneralSettings}>
                        <Col x>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon name="create" style={{ fontSize: '20px', color: '#666' }} />
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                                    Settings
                                </h3>
                            </div>
                        </Col>
                        <Col x>
                            <Icon
                                name={
                                    this.state.generalSettingsExpanded
                                        ? 'keyboard_arrow_up'
                                        : 'keyboard_arrow_down'
                                }
                                style={{ fontSize: '24px', color: '#666' }}
                            />
                        </Col>
                    </Row>
                    {this.state.generalSettingsExpanded && (
                        <div style={{ padding: '16px' }}>
                            <Section n>
                                <Row className="v-center">
                                    <Col
                                        className="grow"
                                        style={{ marginRight: '16px', flex: '2' }}>
                                        {this.renderInput('name')}
                                    </Col>
                                    <Col className="grow" style={{ flex: '1' }}>
                                        {this.renderInput('identifier')}
                                    </Col>
                                </Row>
                            </Section>

                            <Section n>
                                <label style={{ display: 'block', marginBottom: '8px' }}>
                                    Description
                                </label>
                                {this.renderInput('description')}
                            </Section>

                            <Divider m />

                            {/* Settings Section */}
                            <Section n>
                                <Row className="v-center" style={{ marginBottom: '8px' }}>
                                    <Col className="grow" style={{ flex: '1' }}>
                                        {this.renderInput('user_type')}
                                    </Col>
                                    <Col className="grow" style={{ flex: '1', marginLeft: '16px' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                position: 'relative',
                                            }}>
                                            {this.renderInput('expiry_date')}
                                            <div ref={(el) => (this.expiryDateInfoRef = el)}>
                                                <Icon
                                                    style={{ cursor: 'pointer', color: '#666' }}
                                                    fontSize={18}
                                                    onClick={this.toggleExpiryDateInfo}>
                                                    info
                                                </Icon>
                                                {this.state.showExpiryDateInfo && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            marginTop: '8px',
                                                            padding: '12px 16px',
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                            zIndex: 1000,
                                                            minWidth: '280px',
                                                            maxWidth: '350px',
                                                        }}>
                                                        <p
                                                            style={{
                                                                margin: '0 0 8px 0',
                                                                fontWeight: 'bold',
                                                            }}>
                                                            Expiry Date:
                                                        </p>
                                                        <ul
                                                            style={{
                                                                margin: 0,
                                                                paddingLeft: '20px',
                                                                fontSize: '14px',
                                                            }}>
                                                            <li style={{ marginBottom: '4px' }}>
                                                                If set: promo activation only works
                                                                before this date
                                                            </li>
                                                            <li>
                                                                If not set: promo is available
                                                                indefinitely
                                                            </li>
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                {this.shouldShowSpecificProviderIdsInput() && (
                                    <div style={{ marginTop: '16px' }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                fontWeight: '500',
                                            }}>
                                            Specific provider IDs (comma-separated){' '}
                                            <span style={{ color: '#dc3545' }}>*</span>
                                        </label>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                            }}>
                                            <div style={{ flex: 1 }}>
                                                <InputField
                                                    type="text"
                                                    placeholder="Provider IDs (comma-separated, e.g., 123, 456, 789)"
                                                    value={this.getSpecificProviderIdsValue()}
                                                    onChange={this.handleSpecificProviderIdsChange}
                                                    style={{ width: '100%' }}
                                                    error={
                                                        this.getEntity()._errors
                                                            ?.specific_provider_ids || null
                                                    }
                                                />
                                                {this.state.providerIdsValidationResult && (
                                                    <div
                                                        style={{
                                                            marginTop: '4px',
                                                            fontSize: '12px',
                                                            color: this.state
                                                                .providerIdsValidationResult.valid
                                                                ? '#28a745'
                                                                : '#dc3545',
                                                        }}>
                                                        {
                                                            this.state.providerIdsValidationResult
                                                                .message
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={this.validateProviderIds}
                                                disabled={this.state.validatingProviderIds}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    color: this.state.validatingProviderIds
                                                        ? '#666'
                                                        : '#1976d2',
                                                    textDecoration: 'underline',
                                                    cursor: this.state.validatingProviderIds
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                    fontSize: 'inherit',
                                                    fontFamily: 'inherit',
                                                    whiteSpace: 'nowrap',
                                                    opacity: this.state.validatingProviderIds
                                                        ? 0.6
                                                        : 1,
                                                }}>
                                                {this.state.validatingProviderIds
                                                    ? 'Validating...'
                                                    : 'Validate'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Section>

                            <Divider m />

                            {/* Eligibility Rules Section - Moved under General Settings */}
                            <Section n>
                                <h4
                                    style={{
                                        marginBottom: '12px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                    }}>
                                    Eligibility Rules
                                </h4>
                                {this.renderEligibilityRulesPreview()}
                                {this.renderInput('eligibility_rules', false)}
                            </Section>

                            <Divider m />

                            {/* Targeting Section */}
                            <Section n>
                                <h4
                                    style={{
                                        marginBottom: '12px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                    }}>
                                    Targeting
                                </h4>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '16px',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}>
                                    {this.renderPromoStats()}
                                </div>
                                <p
                                    style={{
                                        marginTop: '8px',
                                        fontSize: '14px',
                                        color: '#666',
                                        fontStyle: 'italic',
                                    }}>
                                    Uses the rules above and general settings to calculate
                                    eligibility
                                </p>
                            </Section>
                        </div>
                    )}
                </Section>

                {/* Offers - Always visible */}
                <Section
                    n
                    style={{
                        marginTop: 0,
                        marginBottom: '16px',
                    }}>
                    <Row
                        style={{
                            padding: '12px 16px',
                            backgroundColor: '#f5f5f5',
                            borderBottom: '1px solid #ddd',
                        }}>
                        <Col x>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon
                                        name="create"
                                        style={{ fontSize: '20px', color: '#666' }}
                                    />
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                                        Offers
                                    </h3>
                                </div>
                                {promo._errors?.offers_info && (
                                    <div
                                        style={{
                                            fontSize: '12px',
                                            color: '#dc3545',
                                            marginLeft: '28px',
                                        }}>
                                        {promo._errors.offers_info}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    <div style={{ padding: '16px' }}>
                        <Section n>
                            <div style={{ marginBottom: '16px' }}>{this.renderOffersPreview()}</div>
                            {this.renderInput('offers_info')}
                        </Section>
                    </div>
                </Section>

                <Divider m />

                <Section n>
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}>
                        {promo._isTouched && (
                            <button
                                type="button"
                                className="btn cta"
                                disabled={promo._saving}
                                onClick={this.handleCancel}
                                style={{
                                    backgroundColor: '#9e9e9e',
                                    color: '#fff',
                                    border: 'none',
                                }}>
                                Cancel
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn cta"
                            disabled={promo._saving || !promo._isTouched}
                            onClick={this.save}>
                            {promo._saving ? 'saving..' : 'save'}
                        </button>
                    </div>
                </Section>

                <Modal
                    open={this.state.eligibleProvidersModalOpen}
                    close={this.closeEligibleProvidersModal}
                    maxWidth="1400px">
                    <div
                        style={{ padding: '20px', maxHeight: '90vh', overflow: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}>
                        <h3>{PROVIDERS_MODAL_TEXTS[this.state.selectedModalType].heading}</h3>
                        <p style={{ marginBottom: '20px', color: '#666' }}>
                            {PROVIDERS_MODAL_TEXTS[this.state.selectedModalType].description}
                        </p>
                        {this.state.eligibleProvidersModalOpen && (
                            <EligibleProvidersList
                                promoId={promo.promo_id}
                                modalType={this.state.selectedModalType}
                            />
                        )}
                    </div>
                </Modal>

                <Modal
                    open={this.state.showAssignConfirmation}
                    close={this.cancelAssignPromo}
                    maxWidth="500px">
                    <div
                        style={{ padding: '20px' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Confirm Assignment</h3>
                        <p style={{ marginBottom: '20px', color: '#666' }}>
                            Do you want to assign this promo to{' '}
                            <strong>{this.state.promoStats?.eligible || 0}</strong> eligible
                            provider(s)? The promo will be assigned with pending status.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={this.cancelAssignPromo}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #ddd',
                                    backgroundColor: '#fff',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                }}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={this.confirmAssignPromo}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    backgroundColor: '#ff9800',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                }}>
                                Assign
                            </button>
                        </div>
                    </div>
                </Modal>

                <Modal
                    open={this.state.showUnassignConfirmation}
                    close={this.cancelUnassignPromo}
                    maxWidth="500px">
                    <div
                        style={{ padding: '20px' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Confirm Unassignment</h3>
                        <p style={{ marginBottom: '20px', color: '#666' }}>
                            Do you want to unassign this promo from all assigned providers? This
                            will remove all promo assignments (pending, active, and inactive) from
                            the service_providers_promos table.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={this.cancelUnassignPromo}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #ddd',
                                    backgroundColor: '#fff',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                }}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={this.confirmUnassignPromo}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    backgroundColor: '#dc3545',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                }}>
                                Unassign
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        )
    }
}

PromoEntity.propTypes = {
    entityId: PropTypes.any.isRequired,
}

export default EntityComponentContainer(PromoEntity, {
    store: 'promos',
    name: 'promos',
    saveFunc: savePromo,
    mapStateToProps: (state) => ({
        prospectLabelsMap: state.labels?.entities?.labels || {},
    }),
    mapDispatchToProps: (dispatch) => ({
        fetchServices: (filterId) => dispatch(fetchServices(filterId)),
        fetchLabels: (options) => dispatch(fetchLabels(options)),
    }),
})
